import { p, h, ul, code, tip, info, warn, table, cards, ladder } from './helpers';

const course = {
  id: 'sip-foundations',
  step: 1,
  title: 'SIP Foundations',
  tagline: 'The language every VoIP device speaks: URIs, messages, methods, transactions and dialogs.',
  icon: 'Radio',
  colors: ['#ff3d7f', '#ff9f1c'],
  level: 'Beginner',
  lessons: [
    {
      id: 'what-is-sip',
      title: 'What is SIP?',
      icon: 'Sparkles',
      minutes: 8,
      summary: 'SIP sets up, changes and tears down sessions. It does not carry your voice.',
      blocks: [
        p('The **Session Initiation Protocol (SIP)** is an application-layer signaling protocol defined in **RFC 3261** (2002). Its job is to *create, modify and terminate sessions* between two or more participants. A session can be a phone call, a video call, an instant-messaging conversation or a conference.'),
        p('SIP borrows heavily from HTTP and SMTP. Messages are human-readable text, there are request methods and numeric response codes, and headers look exactly like email headers. If you can read an HTTP request, you can read a SIP message.'),
        cards([
          { icon: 'MessageSquareText', title: 'Signaling', text: 'SIP negotiates who talks to whom, where they are and when the call starts and ends.' },
          { icon: 'FileText', title: 'Session description', text: 'SDP, carried in the SIP body, describes codecs, IP addresses and ports for the media.' },
          { icon: 'AudioLines', title: 'Media', text: 'RTP carries the actual audio and video packets, usually on a completely different path.' },
        ]),
        h('Signaling and media are separate'),
        p('This is the single most important idea in VoIP. SIP messages may travel through several proxies (like Kamailio), while the RTP audio stream typically flows **directly between the two phones**, or through a dedicated media relay. Kamailio is a signaling server: it never decodes audio itself.'),
        info('Where Kamailio fits', 'Kamailio processes SIP messages only. When it needs to touch media (for NAT traversal or recording), it controls an external media relay such as RTPEngine.'),
        h('Transports and ports'),
        table(['Transport', 'Default port', 'Notes'], [
          ['UDP', '5060', 'Most common. Small, fast, but messages over ~1300 bytes should move to TCP.'],
          ['TCP', '5060', 'Reliable stream. Required for large messages.'],
          ['TLS', '5061', 'Encrypted TCP. Uses the `sips:` URI scheme end to end.'],
          ['WebSocket (WS/WSS)', '80 / 443 (any)', 'RFC 7118. Lets browsers speak SIP for WebRTC.'],
          ['SCTP', '5060', 'Rare outside carrier networks.'],
        ]),
        h('SIP URIs: the addresses of SIP'),
        p('Every user and server is identified by a URI that looks like an email address with a scheme in front:'),
        code('text', `
sip:alice@atlanta.example.com
sip:alice@atlanta.example.com:5080;transport=tcp
sips:bob@biloxi.example.com
sip:+14155550100@gw.example.com;user=phone
tel:+14155550100`),
        ul([
          '**scheme**: `sip`, `sips` (TLS required on every hop) or `tel` (a phone number).',
          '**user part**: the account or number, for example `alice` or `+14155550100`.',
          '**host part**: a domain name or IP address, optionally with a port.',
          '**URI parameters**: `;transport=tcp`, `;lr` (loose routing), `;user=phone` and others.',
        ]),
        tip('Address of Record vs Contact', 'Your **Address of Record (AoR)**, such as `sip:alice@example.com`, is your public identity. Your **Contact** is where your phone actually is right now, such as `sip:alice@192.0.2.44:52311`. A registrar maps one to the other.'),
      ],
    },
    {
      id: 'sip-elements',
      title: 'SIP network elements',
      icon: 'Network',
      minutes: 9,
      summary: 'User agents, proxies, registrars, redirect servers and B2BUAs, and what each one is allowed to do.',
      blocks: [
        p('RFC 3261 defines a small cast of logical roles. One physical server can play several roles at once; Kamailio is often a proxy, a registrar and a redirect server in a single process.'),
        cards([
          { icon: 'Smartphone', title: 'User Agent (UA)', text: 'An endpoint: softphone, desk phone, gateway. It acts as a UAC when it sends a request and as a UAS when it answers one.' },
          { icon: 'Route', title: 'Proxy server', text: 'Receives requests and forwards them closer to the destination. It may add Via and Record-Route headers but must not change the body or the dialog.' },
          { icon: 'BookUser', title: 'Registrar', text: 'Accepts REGISTER requests and stores AoR to Contact bindings in the location service.' },
          { icon: 'Signpost', title: 'Redirect server', text: 'Answers with a 3xx response that tells the caller where to try instead. It never forwards.' },
          { icon: 'Database', title: 'Location service', text: 'The database the registrar writes to and the proxy reads from. In Kamailio this is the usrloc module.' },
          { icon: 'Repeat', title: 'B2BUA', text: 'Back-to-back user agent. Terminates one call leg and originates a new one. PBXs and SBCs work this way.' },
        ]),
        h('Three flavors of proxy'),
        table(['Type', 'Keeps state for', 'Kamailio functions', 'Typical use'], [
          ['Stateless', 'Nothing', '`forward()`, `sl_send_reply()`', 'Very high throughput load balancers, simple replies'],
          ['Transaction stateful', 'One request and its responses', '`t_relay()` from the **tm** module', 'Forking, failover, retransmission handling'],
          ['Dialog stateful', 'The whole call, INVITE to BYE', 'The **dialog** module', 'Call limits, active call counts, call timers'],
        ]),
        h('Proxy vs B2BUA'),
        p('A proxy stays transparent: the Call-ID, tags and SDP that leave the proxy are the same ones that arrived. A B2BUA creates a brand-new dialog on the other side, with a new Call-ID, so it can rewrite anything, transcode media, play prompts or record calls.'),
        table(['', 'Proxy (Kamailio)', 'B2BUA (Asterisk, FreeSWITCH, SBC)'], [
          ['Call legs', 'One end-to-end dialog', 'Two independent dialogs'],
          ['Media handling', 'Never, unless it controls a relay', 'Often anchors, transcodes, records'],
          ['Performance', 'Thousands of calls per second', 'Lower, media processing costs CPU'],
          ['Features', 'Routing, auth, registration, load balancing', 'IVR, voicemail, queues, conferencing'],
        ]),
        tip('The classic architecture', 'Put Kamailio at the edge to handle registrations, security and load balancing, and let it dispatch calls to a farm of Asterisk or FreeSWITCH servers that provide PBX features. You will build exactly this in Step 7.'),
      ],
    },
    {
      id: 'message-anatomy',
      title: 'Anatomy of a SIP message',
      icon: 'ScanText',
      minutes: 12,
      summary: 'Start line, headers, a blank line and an optional body. Learn the headers every request must carry.',
      blocks: [
        p('Every SIP message is either a **request** or a **response**. Both share the same layout: a start line, a block of headers, an empty line (CRLF) and an optional body.'),
        code('sip', `
INVITE sip:bob@biloxi.example.com SIP/2.0
Via: SIP/2.0/UDP pc33.atlanta.example.com:5060;branch=z9hG4bK776asdhds
Max-Forwards: 70
To: Bob <sip:bob@biloxi.example.com>
From: Alice <sip:alice@atlanta.example.com>;tag=1928301774
Call-ID: a84b4c76e66710@pc33.atlanta.example.com
CSeq: 314159 INVITE
Contact: <sip:alice@pc33.atlanta.example.com>
User-Agent: Linphone/5.2
Content-Type: application/sdp
Content-Length: 142

v=0
o=alice 2890844526 2890844526 IN IP4 pc33.atlanta.example.com
s=-
c=IN IP4 192.0.2.101
t=0 0
m=audio 49172 RTP/AVP 0
a=rtpmap:0 PCMU/8000`, 'An INVITE from Alice to Bob (based on RFC 3261 section 4)'),
        h('The start line'),
        ul([
          'A **request line** has three parts: `METHOD Request-URI SIP/2.0`. The Request-URI (R-URI) is where the request is going right now. Proxies route on it, and Kamailio exposes it as `$ru`.',
          'A **status line** has three parts as well: `SIP/2.0 200 OK`, the version, a three-digit code and a human-readable reason phrase.',
        ]),
        h('The mandatory headers'),
        table(['Header', 'Compact', 'Purpose'], [
          ['Via', 'v', 'The path the request took so far. Each hop adds one. Responses retrace the Via path backwards. The `branch` parameter identifies the transaction and always starts with the magic cookie `z9hG4bK`.'],
          ['Max-Forwards', '', 'Hop counter, starts at 70. Every proxy decrements it; at 0 the request is rejected with 483 Too Many Hops. Prevents loops.'],
          ['From', 'f', 'Who started the request (logical identity) plus a random `tag`.'],
          ['To', 't', 'The logical recipient. The callee adds its own `tag` in the response.'],
          ['Call-ID', 'i', 'Globally unique identifier for the call or registration.'],
          ['CSeq', '', 'A sequence number plus the method. Increments for each new request in a dialog.'],
          ['Contact', 'm', 'The direct URI where this UA can be reached. Required in INVITE and REGISTER.'],
          ['Content-Length', 'l', 'Size of the body in bytes. Mandatory over TCP.'],
          ['Content-Type', 'c', 'Body type, usually `application/sdp`.'],
        ]),
        h('Routing headers'),
        ul([
          '**Record-Route**: a proxy that wants to stay in the signaling path for the rest of the dialog inserts its own URI here.',
          '**Route**: the UA copies the Record-Route set into Route headers on every later request in the dialog, forcing it through the same proxies.',
          'The `;lr` parameter on these URIs marks **loose routing** (RFC 3261 style). Kamailio always uses it.',
        ]),
        h('A matching response'),
        code('sip', `
SIP/2.0 200 OK
Via: SIP/2.0/UDP pc33.atlanta.example.com:5060;branch=z9hG4bK776asdhds;received=192.0.2.101
To: Bob <sip:bob@biloxi.example.com>;tag=a6c85cf
From: Alice <sip:alice@atlanta.example.com>;tag=1928301774
Call-ID: a84b4c76e66710@pc33.atlanta.example.com
CSeq: 314159 INVITE
Contact: <sip:bob@192.0.2.4>
Content-Type: application/sdp
Content-Length: 131`),
        p('Notice what the response keeps (Via, From, Call-ID, CSeq are copied) and what it adds: Bob\'s `tag` on the To header and his own Contact. The `received` parameter on the Via was added because the source IP differed from the Via host.'),
        tip('Read the tags', 'A request with a tag on the To header belongs to an existing dialog. Kamailio checks this with `has_totag()`, one of the most used functions in any config.'),
      ],
    },
    {
      id: 'methods-and-codes',
      title: 'Methods and response codes',
      icon: 'ListChecks',
      minutes: 10,
      summary: 'The 14 request methods you will meet and the six classes of responses.',
      blocks: [
        h('Request methods'),
        table(['Method', 'Defined in', 'What it does'], [
          ['INVITE', 'RFC 3261', 'Starts a session, or changes one (re-INVITE).'],
          ['ACK', 'RFC 3261', 'Confirms the final response to an INVITE. Completes the three-way handshake.'],
          ['BYE', 'RFC 3261', 'Ends an established session.'],
          ['CANCEL', 'RFC 3261', 'Cancels a pending INVITE that has not received a final response.'],
          ['REGISTER', 'RFC 3261', 'Binds an AoR to a Contact at the registrar.'],
          ['OPTIONS', 'RFC 3261', 'Asks a UA or server about capabilities. Widely used as a keepalive ping.'],
          ['PRACK', 'RFC 3262', 'Acknowledges reliable provisional responses (100rel).'],
          ['SUBSCRIBE', 'RFC 6665', 'Subscribes to an event, like presence or message waiting.'],
          ['NOTIFY', 'RFC 6665', 'Delivers event state to a subscriber.'],
          ['PUBLISH', 'RFC 3903', 'Uploads event state (e.g. presence) to a server.'],
          ['INFO', 'RFC 6086', 'Sends mid-call application information, sometimes DTMF.'],
          ['REFER', 'RFC 3515', 'Asks the recipient to contact a third party. Used for call transfer.'],
          ['MESSAGE', 'RFC 3428', 'Sends an instant message (pager mode).'],
          ['UPDATE', 'RFC 3311', 'Modifies session parameters before the call is answered.'],
        ]),
        h('Response classes'),
        cards([
          { icon: 'Hourglass', title: '1xx Provisional', text: '100 Trying, 180 Ringing, 183 Session Progress. Not final; the transaction continues.' },
          { icon: 'CircleCheck', title: '2xx Success', text: '200 OK, 202 Accepted. The request worked.' },
          { icon: 'Forward', title: '3xx Redirection', text: '301 Moved Permanently, 302 Moved Temporarily. Try the Contact in the response.' },
          { icon: 'CircleAlert', title: '4xx Client error', text: 'The request was wrong or cannot be served by this server. Try changing it.' },
          { icon: 'ServerCrash', title: '5xx Server error', text: 'The server failed. Another server might succeed.' },
          { icon: 'Ban', title: '6xx Global failure', text: '600 Busy Everywhere, 603 Decline. Do not try anywhere else.' },
        ]),
        h('Codes you will see every day'),
        table(['Code', 'Reason', 'Usual cause'], [
          ['100', 'Trying', 'Sent hop-by-hop to stop INVITE retransmissions.'],
          ['180', 'Ringing', 'The callee is alerting.'],
          ['183', 'Session Progress', 'Early media, such as a ringback tone or announcement.'],
          ['200', 'OK', 'Success.'],
          ['401', 'Unauthorized', 'A registrar or UAS challenges for credentials (WWW-Authenticate).'],
          ['403', 'Forbidden', 'Understood but refused, e.g. wrong password or relaying denied.'],
          ['404', 'Not Found', 'User does not exist or is not registered.'],
          ['407', 'Proxy Authentication Required', 'A proxy challenges for credentials (Proxy-Authenticate).'],
          ['408', 'Request Timeout', 'No final answer in time (Timer B or F fired).'],
          ['480', 'Temporarily Unavailable', 'User known but not reachable right now.'],
          ['483', 'Too Many Hops', 'Max-Forwards reached zero, usually a routing loop.'],
          ['486', 'Busy Here', 'Callee is busy.'],
          ['487', 'Request Terminated', 'INVITE was cancelled by CANCEL.'],
          ['488', 'Not Acceptable Here', 'No common codec or unacceptable SDP.'],
          ['500', 'Server Internal Error', 'Something broke on the server.'],
          ['503', 'Service Unavailable', 'Overloaded or no destination available.'],
          ['603', 'Decline', 'Callee explicitly rejected the call.'],
        ]),
        warn('401 vs 407', 'A registrar or the final endpoint challenges with **401** and the `WWW-Authenticate` header; the client answers with `Authorization`. A proxy challenges with **407** and `Proxy-Authenticate`; the client answers with `Proxy-Authorization`. Kamailio\'s default config uses 401 for REGISTER and 407 for INVITE.'),
      ],
    },
    {
      id: 'transactions-dialogs',
      title: 'Transactions and dialogs',
      icon: 'Layers',
      minutes: 12,
      summary: 'A transaction is one request and its answers. A dialog is the peer-to-peer relationship that lasts for the whole call.',
      blocks: [
        h('Transactions'),
        p('A **transaction** is one request plus every response it triggers, ending with a final response (and the ACK, for a non-2xx INVITE answer). Transactions are matched by the `branch` parameter of the top Via header together with the CSeq method.'),
        ul([
          '**INVITE transaction**: may last a long time while the phone rings. The client retransmits the INVITE over UDP (Timer A) until it gets any response, and gives up after Timer B = 64 x T1 = **32 seconds**.',
          '**Non-INVITE transaction**: REGISTER, BYE, OPTIONS and the rest. Retransmitted with Timer E, times out with Timer F, also 32 seconds by default.',
          '**T1** is the round-trip estimate, 500 ms by default. **T2** (4 s) caps the retransmission interval for non-INVITE requests.',
        ]),
        h('Dialogs'),
        p('A **dialog** is a long-lived relationship between two UAs. It is created by a 2xx (or 101-199 with a To tag) response to an INVITE and is identified by three values:'),
        code('text', `
Dialog ID = Call-ID + From tag + To tag`),
        p('Requests that create a dialog (an INVITE without a To tag) are called **initial requests**. Requests inside an existing dialog (re-INVITE, BYE, in-dialog ACK) carry both tags and are called **in-dialog** or **sequential** requests. Kamailio scripts route these two groups very differently, as you will see in Step 5.'),
        ladder('One call, three transactions, one dialog', ['Alice', 'Bob'], [
          ['Alice', 'Bob', 'INVITE  (transaction 1)', 'req'],
          ['Bob', 'Alice', '180 Ringing', 'resp'],
          ['Bob', 'Alice', '200 OK  (dialog created)', 'resp'],
          ['Alice', 'Bob', 'ACK  (transaction 2)', 'req'],
          ['Alice', 'Bob', 'RTP audio', 'media'],
          ['Bob', 'Alice', 'BYE  (transaction 3)', 'req'],
          ['Alice', 'Bob', '200 OK  (dialog ends)', 'resp'],
        ]),
        h('Why ACK is special'),
        ul([
          'The ACK for a **non-2xx** final response (e.g. 486 Busy) belongs to the INVITE transaction. It is hop-by-hop: each proxy that is transaction-stateful generates it itself.',
          'The ACK for a **2xx** response is a separate transaction that travels end-to-end, following the Route set like any in-dialog request.',
        ]),
        h('CANCEL'),
        p('CANCEL is a hop-by-hop request that uses the **same branch** as the INVITE it cancels, so each proxy can match it to the pending transaction. The canceled INVITE gets a 487 Request Terminated, and the CANCEL itself gets a 200 OK.'),
        info('Stateful matters', 'Matching CANCELs, absorbing retransmissions, generating hop-by-hop ACKs and failing over to another destination all require transaction state. That is why almost every Kamailio config loads the **tm** module and calls `t_relay()`.'),
      ],
    },
  ],
  quiz: [
    { q: 'Which protocol carries the actual voice packets in a SIP call?', options: ['SIP', 'SDP', 'RTP', 'HTTP'], answer: 2, explain: 'SIP is signaling, SDP describes the media, and RTP transports the audio and video.' },
    { q: 'What do the three values Call-ID, From tag and To tag identify together?', options: ['A transaction', 'A dialog', 'A registration', 'A branch'], answer: 1, explain: 'A dialog is identified by Call-ID plus both tags. Transactions are identified by the Via branch.' },
    { q: 'Which magic cookie must every RFC 3261 branch parameter start with?', options: ['z9hG4bK', 'sip:', 'lr', 'tag='], answer: 0, explain: 'The branch begins with z9hG4bK so proxies know it was generated by an RFC 3261 compliant element.' },
    { q: 'A proxy challenges an INVITE for credentials. Which response does it send?', options: ['401 Unauthorized', '403 Forbidden', '407 Proxy Authentication Required', '488 Not Acceptable Here'], answer: 2, explain: 'Proxies use 407 with Proxy-Authenticate. Registrars and UAS use 401 with WWW-Authenticate.' },
    { q: 'What does a proxy insert to stay in the signaling path for the whole dialog?', options: ['Via', 'Contact', 'Record-Route', 'Max-Forwards'], answer: 2, explain: 'Record-Route makes the endpoints build a Route set that sends later in-dialog requests through the proxy.' },
  ],
};

export default course;
